import { useCallback, useMemo } from "react";
import { arrayUnion, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useWedding } from "./useWedding";
import type { GuestGroup } from "@amazing-wedding/shared-types";

/**
 * Group management lives on the wedding doc itself (Wedding.groups[]) rather
 * than a subcollection — see Plan 03 §"Tech decisions §2".
 */
export function useGroups() {
  const { wedding } = useWedding();
  // Memoise so the array reference is stable while wedding.groups itself is.
  // Without this the useCallback deps below would invalidate every render.
  const groups = useMemo(() => wedding?.groups ?? [], [wedding?.groups]);

  const weddingRef = wedding ? doc(db, "weddings", wedding.id) : null;

  const addGroup = useCallback(
    async (group: GuestGroup) => {
      if (!weddingRef) throw new Error("No wedding loaded.");
      await updateDoc(weddingRef, {
        groups: arrayUnion(group),
        updatedAt: serverTimestamp(),
      });
    },
    [weddingRef],
  );

  const updateGroup = useCallback(
    async (groupId: string, patch: Partial<Omit<GuestGroup, "id">>) => {
      if (!weddingRef || !wedding) throw new Error("No wedding loaded.");
      const next = groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g));
      await updateDoc(weddingRef, {
        groups: next,
        updatedAt: serverTimestamp(),
      });
    },
    [weddingRef, wedding, groups],
  );

  const deleteGroup = useCallback(
    // Rewrites the full array by id rather than `arrayRemove(victim)` so a
    // rename-then-delete race (server holds the old shape, local has the new)
    // can never silently no-op.
    async (groupId: string) => {
      if (!weddingRef) throw new Error("No wedding loaded.");
      const next = groups.filter((g) => g.id !== groupId);
      if (next.length === groups.length) return;
      await updateDoc(weddingRef, {
        groups: next,
        updatedAt: serverTimestamp(),
      });
    },
    [weddingRef, groups],
  );

  return { groups, addGroup, updateGroup, deleteGroup };
}
